import { FiltersMap, RawFilters } from '../model';
import { Chance } from 'chance';
import orderBy from 'lodash/orderBy';
import shuffle from 'lodash/shuffle';
import { ListIteratee } from 'lodash';
import { EventEmitter } from 'events';
import { TypedEmitter } from '../util';
import { hashQueryKey } from 'react-query/core';

interface Order<T> {
  fieldName: ListIteratee<T>;
  order: 'asc' | 'desc';
}

interface IndexedItem<T> {
  item: T;
  index: number;
}

export type TransformPatch<T> = (item: T) => Partial<T>;

export interface BackendQuery<F extends FiltersMap = FiltersMap> {
  /**
   * Number of items to fetch for each page
   */
  limit: number;
  /**
   * The index of the item to start fetching items from.<br>
   * Relevant for APIs that support offset pagination.<br>
   * Not available on `CursorQuery`
   */
  offset?: number;
  page?: number;
  /**
   * Current search term (defaults to `undefined` for empty search)
   */
  search?: string;
  /**
   * The `cursor` returned from the server, to be sent back to it in the next call.<br>
   * Relevant for APIs that support cursor pagination.<br>
   * Not available on `OffsetQuery`
   */
  cursor?: string | null;
  /**
   * Current state of the filters. Each key holds the value of the filter, according to its type, defined in [CollectionConfig](./?path=/story/common-hooks--usecollection).
   */
  filters?: RawFilters<F>;

  /**
   * Current sort applied to the collection.
   */
  sort?: { fieldName: string; order: 'asc' | 'desc' }[];

  /**
   * Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned.
   */
  fields?: string[];
  /**
   * Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned.
   */
  fieldsset?: string[];
}

export interface Delay {
  min: number;
  max: number;
}

interface SubmitParams {
  delay?: Delay;
  throwError?: () => unknown;
}

interface MoveEvent<T, F extends FiltersMap> {
  from: IndexedItem<T>;
  after: IndexedItem<T> | null;
  over: IndexedItem<T>;
  filters: RawFilters<F>;
}

export interface InMemoryBackendParams<T, F extends FiltersMap = FiltersMap> {
  paginationMode?: 'offset' | 'cursor';
  /**
   * A callback function for each item creation
   */
  createOne: (index: number) => T;
  /**
   * A callback function that accepts an item and returns a unique ID of it. typically `item.id`.
   */
  itemKey: (item: T) => string;
  /**
   * A function that accepts a subset of [ComputedQuery](./?path=/story/common-types--computedquery) sent by the client and returns another function that accepts an item and should return a boolean that indicates whether the item passes the query filtering or not.
   */
  predicate?: (query: BackendQuery<F>) => (item: T) => boolean;
  /**
   * A function that accepts a subset of [ComputedQuery](./?path=/story/common-types--computedquery) sent by the client and returns another function that accepts an item and should returns a list of rules to order the items by
   */
  orderBy?: (query: BackendQuery<F>) => Order<T>[];

  /**
   * A function that accepts a subset of [ComputedQuery](./?path=/story/common-types--computedquery) sent by the client and returns a list of fields to project from the item
   */
  projection?: (query: BackendQuery<F>) => (item: T) => T;
  /**
   * Simulates API latency
   * @default { min: 0, max: 5 }
   */
  delay?: { min: number; max: number };
  /**
   * Total number of items to store in the backend
   */
  total: number;
  /**
   * Enable total for the backend. Defaults to "true"
   */
  enableTotal?: boolean;

  excludeOrdinalFilters?: string[];
}

export class InMemoryBackend<T, F extends FiltersMap = FiltersMap> {
  readonly createOne: InMemoryBackendParams<T, F>['createOne'];
  readonly itemKey: InMemoryBackendParams<T, F>['itemKey'];
  readonly predicate: (query: BackendQuery<F>) => (item: T) => boolean;
  readonly orderBy?: (query: BackendQuery<F>) => Order<T>[];
  readonly projection;
  readonly initialTotal: number;
  readonly delay: { min: number; max: number };
  readonly paginationMode;
  readonly enableTotal?: boolean;

  readonly excludeOrdinalFilters;

  readonly events = new EventEmitter() as TypedEmitter<{
    updatedMany: () => void;
    updateAll: () => void;
    deleteAll: () => void;
    fetchDataStart: (query: BackendQuery<F>) => void | Promise<void>;
    fetchTotalStart: (query: BackendQuery<F>) => void | Promise<void>;
    moveStart: (events: MoveEvent<T, F>[]) => void | Promise<void>;
  }>;

  map: Map<string, T>;

  readonly chance = new Chance();

  constructor(params: InMemoryBackendParams<T, F>) {
    this.enableTotal = params.enableTotal ?? true;
    this.createOne = params.createOne;
    this.itemKey = params.itemKey;
    this.paginationMode = params.paginationMode;
    this.predicate = params.predicate ?? (() => () => true);
    this.orderBy = params.orderBy;
    this.projection = params.projection;
    this.initialTotal = params.total;
    this.delay = params.delay ?? { min: 0, max: 5 };
    this.excludeOrdinalFilters = params.excludeOrdinalFilters ?? [];
    this.map = new Map<string, T>(
      this.newList(params.total).map((item) => [this.itemKey(item), item]),
    );
  }

  private async simulateAsyncDelay({ delay, throwError }: SubmitParams = {}) {
    const { chance } = this;
    await new Promise((resolve) =>
      setTimeout(resolve, chance.integer(delay ?? this.delay)),
    ); // simulate async

    await throwError?.();
  }

  private newList(length: number) {
    return new Array(length)
      .fill(null)
      .map((_, index) => this.createOne(index));
  }

  fetchDataSync(query: BackendQuery<F>) {
    const { itemKey, predicate, orderBy: getOrderBy, paginationMode } = this;

    const ordinal = this._getOrdinal(query.filters ?? {});

    const allItems = this.values;

    const orders = getOrderBy?.(query);

    let itemsAfterOrdering;

    if (orders?.length) {
      itemsAfterOrdering = orderBy(
        allItems,
        orders.map(({ fieldName }) => fieldName),
        orders.map(({ order }) => order),
      );
    } else if (ordinal.size) {
      itemsAfterOrdering = orderBy(
        allItems,
        (item) => ordinal.get(itemKey(item)),
        ['asc'],
      );
    } else {
      itemsAfterOrdering = allItems;
    }

    const itemsAfterFiltering = itemsAfterOrdering.filter(predicate(query));

    let offset;

    if (paginationMode === 'cursor') {
      offset = query.cursor
        ? itemsAfterFiltering.findIndex(
            (value) => itemKey(value) === query.cursor,
          )
        : 0;
    } else {
      offset = query.cursor
        ? itemsAfterFiltering.findIndex(
            (value) => itemKey(value) === query.cursor,
          )
        : query.offset ?? 0;
    }

    const itemsAfterSlicing = itemsAfterFiltering.slice(
      offset,
      offset + query.limit,
    );

    const cursorItem = itemsAfterFiltering[offset + query.limit] as
      | T
      | undefined;

    const itemsAfterFieldsProjection = this.projection
      ? itemsAfterSlicing.map(this.projection(query))
      : itemsAfterSlicing;

    return {
      items: itemsAfterFieldsProjection,
      ...(this.enableTotal
        ? { total: itemsAfterFiltering.length, available: allItems.length }
        : {}),
      cursor: cursorItem && itemKey(cursorItem),
    };
  }

  fetchData = async (query: BackendQuery<F>) => {
    const fetchDataStartPromises = this.events
      .listeners('fetchDataStart')
      .map((listener) => listener(query));

    if (fetchDataStartPromises.length) {
      await Promise.all(fetchDataStartPromises);
    }

    await this.simulateAsyncDelay();
    return this.fetchDataSync(query);
  };

  fetchTotal = async (query: BackendQuery<F>) => {
    const fetchTotalStartPromises = this.events
      .listeners('fetchTotalStart')
      .map((listener) => listener(query));

    if (fetchTotalStartPromises.length) {
      await Promise.all(fetchTotalStartPromises);
    }
    await this.simulateAsyncDelay();
    return this.fetchDataSync({ ...query, limit: 1 }).total ?? 0;
  };

  deleteMany = async (items: T[], params?: SubmitParams) => {
    await this.simulateAsyncDelay(params);
    items.forEach((item) => this.map.delete(this.itemKey(item)));
  };

  async deleteAll(
    params: {
      delay?: Delay;
      throwError?: () => unknown;
      filters?: RawFilters<F>;
    } = {},
  ) {
    const { filters = {} } = params;

    await this.simulateAsyncDelay(params);
    const { predicate, itemKey } = this;

    const isItemDeleted = predicate({
      filters,
      limit: 1,
    });

    for (const item of this.values) {
      if (isItemDeleted(item)) {
        this.map.delete(itemKey(item));
      }
    }

    this.events.emit('deleteAll');
  }

  clear() {
    this.map.clear();
  }

  update = async (item: T) => {
    await this.simulateAsyncDelay();
    this.map.set(this.itemKey(item), item);
  };

  async updateMany(items: T[], params: SubmitParams = {}) {
    await this.simulateAsyncDelay(params);
    for (const item of items) {
      this.map.set(this.itemKey(item), item);
    }
    this.events.emit('updatedMany');
  }
  readonly categoriesOrdinal: Map<string, Map<string, number>> = new Map();

  _getOrdinal(filters: RawFilters<F>) {
    const { categoriesOrdinal, excludeOrdinalFilters } = this;

    const category = hashQueryKey([
      {
        filters: Object.fromEntries(
          Object.entries(filters).filter(
            ([key]) => !excludeOrdinalFilters.includes(key),
          ),
        ),
      },
    ]);

    const ordinal =
      categoriesOrdinal.get(category) ?? new Map<string, number>();

    if (!categoriesOrdinal.has(category)) {
      categoriesOrdinal.set(category, ordinal);
    }

    return ordinal;
  }

  async move(
    events: MoveEvent<T, F>[],
    params: {
      delay?: Delay;
      throwError?: () => unknown;
    } = {},
  ) {
    const { itemKey } = this;

    const moveStartPromises = this.events
      .listeners('moveStart')
      .map((listener) => listener(events));

    if (moveStartPromises.length) {
      await Promise.all(moveStartPromises);
    }

    await this.simulateAsyncDelay(params);

    for (const { from, after, filters } of events) {
      const ordinal = this._getOrdinal(filters);

      const array = ordinal.size
        ? orderBy(this.values.slice(), (item) => ordinal.get(itemKey(item)), [
            'asc',
          ])
        : this.values.slice();

      const fromKey = itemKey(from.item);
      const toKey = after != null ? itemKey(after.item) : null;

      const fromIndex = array.findIndex((item) => fromKey === itemKey(item));
      if (fromIndex !== -1) {
        array.splice(fromIndex, 1);
      }

      if (toKey != null) {
        const toIndex = array.findIndex((item) => toKey === itemKey(item));
        if (toIndex !== -1) {
          array.splice(toIndex + 1, 0, from.item);
        }
      } else {
        array.splice(0, 0, from.item);
      }

      ordinal.clear();
      for (let i = 0, len = array.length; i < len; i++) {
        ordinal.set(itemKey(array[i]), i);
      }
    }
  }

  async updateAll(
    patch: TransformPatch<T>,
    params: {
      delay?: Delay;
      throwError?: () => unknown;
      filters?: RawFilters<F>;
    } = {},
  ) {
    const { filters = {} } = params;

    await this.simulateAsyncDelay(params);
    const { predicate } = this;

    const itemsAfterFiltering = this.values.filter(
      predicate({
        filters,
        limit: 1,
      }),
    );

    const patchedItems = itemsAfterFiltering.map((value) => ({
      ...value,
      ...patch(value),
    }));

    for (const item of patchedItems) {
      this.map.set(this.itemKey(item), item);
    }

    this.events.emit('updateAll');
  }

  async unshift(newItems: T[]) {
    await this.simulateAsyncDelay();
    const map = new Map<string, T>();

    for (const item of newItems) {
      map.set(this.itemKey(item), item);
    }

    for (const [key, value] of Array.from(this.map.entries())) {
      map.set(key, value);
    }

    this.map = map;
  }

  async push(newItems: T[]) {
    await this.simulateAsyncDelay();

    for (const item of newItems) {
      this.map.set(this.itemKey(item), item);
    }
  }

  elementAt = (index: number) => this.values[index];

  getRandomItems(
    count: number,
    { limit, page }: { limit: number; page: number },
  ) {
    const { chance, values } = this;
    const offset = (page - 1) * limit;
    return chance.pickset(values.slice(offset, offset + limit), count);
  }

  randomItem = (predicate = (_: T) => true) => {
    const index = Math.floor(Math.random() * this.available);
    return {
      index,
      item: shuffle(this.values).find(predicate),
    };
  };

  get values() {
    return Array.from(this.map.values());
  }

  get available() {
    return this.map.size;
  }

  get(key?: string): T | undefined {
    if (key == null) {
      return undefined;
    }
    return this.map.get(key);
  }
}
